import { Handle, Position, useConnection, type NodeProps } from '@xyflow/react';
import { Box } from 'lucide-react';
import { memo } from 'react';
import { getCatalogEntry } from '../../lib/architectureNodeCatalog';

export interface ServiceNodeData {
  kind: string;
  label: string;
  color?: string;
  isSelected: boolean;
  /** During the guided story: 'focus' = a part of the current step, 'dim' = everything else. */
  tour?: 'focus' | 'dim' | null;
  /** Colour behind the node (the canvas, or the tinted fill of the group it sits in); hides connectors passing under it. */
  backdrop?: string;
  [key: string]: unknown;
}

/** Edges attach to the icon tile, so FlowEdge needs to know where the tile sits inside the node box. */
export const SERVICE_TILE_SIZE = 64;
/** Rendered size of the icon glyph itself; connectors end on its outline, not on the larger tile. */
export const SERVICE_ICON_SIZE = 40;
/** Opaque backing behind the glyph (matches the canvas colour) so connectors passing under a node are hidden by it. */
export const SERVICE_ICON_PATCH = SERVICE_ICON_SIZE + 4;

const handleClass =
  '!h-2.5 !w-2.5 !border-2 !border-white !bg-sky-500 opacity-0 transition-opacity group-hover:opacity-100 dark:!border-gray-900';

/**
 * Basic-mode node: just the technology icon with a caption — no card. The whole icon is a drop target for
 * connectors (see the "drop" handle) and edges are drawn to the icon's border from whatever angle they arrive.
 */
function ServiceNode({ data }: NodeProps & { data: ServiceNodeData }) {
  const entry = getCatalogEntry(data.kind);
  const Icon = entry?.icon.icon ?? Box;
  const accent = data.color ?? entry?.color ?? '#94a3b8';
  // The full-size drop handle only intercepts the pointer while a connector is being dragged,
  // so it never gets in the way of moving or selecting the node.
  const connecting = useConnection((c) => c.inProgress);

  return (
    <div className={`group flex h-full w-full flex-col items-center transition-opacity duration-500 ${data.tour === 'dim' ? 'opacity-[0.18]' : ''}`}>
      <div
        className={`relative flex items-center justify-center rounded-2xl transition ${
          data.tour === 'focus'
            ? 'ring-2 ring-amber-400 shadow-[0_0_28px_rgba(251,191,36,0.6)]'
            : data.isSelected
              ? 'ring-2 ring-sky-500 dark:ring-sky-400'
              : ''
        }`}
        style={{ width: SERVICE_TILE_SIZE, height: SERVICE_TILE_SIZE }}
      >
        <Handle
          type="target"
          position={Position.Top}
          id="drop"
          isConnectableStart={false}
          className="!left-0 !top-0 !h-full !w-full !translate-x-0 !translate-y-0 !rounded-2xl !border-0 !bg-transparent"
          style={{ pointerEvents: connecting ? 'all' : 'none' }}
        />
        <Handle type="source" position={Position.Top} id="top" isConnectableEnd={false} className={handleClass} />
        <Handle type="source" position={Position.Right} id="right" isConnectableEnd={false} className={handleClass} />
        <Handle type="source" position={Position.Bottom} id="bottom" isConnectableEnd={false} className={handleClass} />
        <Handle type="source" position={Position.Left} id="left" isConnectableEnd={false} className={handleClass} />
        <span
          className="flex items-center justify-center rounded-lg bg-canvas dark:bg-gray-950"
          style={{ width: SERVICE_ICON_PATCH, height: SERVICE_ICON_PATCH, ...(data.backdrop ? { backgroundColor: data.backdrop } : {}) }}
        >
          <Icon className="drop-shadow-sm" style={{ color: accent, width: SERVICE_ICON_SIZE, height: SERVICE_ICON_SIZE }} />
        </span>
      </div>
      <span
        className={`mt-1.5 line-clamp-2 w-full break-words text-center text-xs font-medium leading-tight text-gray-700 dark:text-gray-200 ${
          data.backdrop ? '' : '[text-shadow:0_0_4px_#f5f7fb,0_0_4px_#f5f7fb,0_0_2px_#f5f7fb] dark:[text-shadow:0_0_4px_#070b14,0_0_4px_#070b14,0_0_2px_#070b14]'
        }`}
        style={data.backdrop ? { textShadow: `0 0 4px ${data.backdrop}, 0 0 4px ${data.backdrop}, 0 0 2px ${data.backdrop}` } : undefined}
      >{data.label}</span>
    </div>
  );
}

export default memo(ServiceNode);
